import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, CalendarClock } from "lucide-react";
import { Button, Input, Checkbox, Card, CardContent, Modal, StatusBadge, LoadingBlock, EmptyState, toast, ConfirmDialog } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { DAYS } from "./constants";
import { Field } from "../onboarding/_ui";

const MODALITIES = [
  { key: "in_person", label: "In person" }, { key: "telephone", label: "Telephone" },
  { key: "video", label: "Video" }, { key: "onsite", label: "Onsite" },
];

const EMPTY = {
  name: "", duration_minutes: "60", buffer_before_minutes: "0", buffer_after_minutes: "0",
  minimum_notice_minutes: "120", maximum_advance_days: "60",
  in_person: true, telephone: false, video: false, onsite: false, active: true,
};

export function AppointmentTypesPanel() {
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [windows, setWindows] = useState<Record<number, { start: string; end: string }>>({ 1: { start: "08:00", end: "17:00" }, 2: { start: "08:00", end: "17:00" }, 3: { start: "08:00", end: "17:00" }, 4: { start: "08:00", end: "17:00" }, 5: { start: "08:00", end: "17:00" } });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.atList().then(setRows).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null); setForm({ ...EMPTY });
    setWindows({ 1: { start: "08:00", end: "17:00" }, 2: { start: "08:00", end: "17:00" }, 3: { start: "08:00", end: "17:00" }, 4: { start: "08:00", end: "17:00" }, 5: { start: "08:00", end: "17:00" } });
    setOpen(true);
  };
  const openEdit = (a: Record<string, unknown>) => {
    setEditing(a);
    setForm({
      name: (a.name as string) ?? "", duration_minutes: String(a.duration_minutes ?? 60),
      buffer_before_minutes: String(a.buffer_before_minutes ?? 0), buffer_after_minutes: String(a.buffer_after_minutes ?? 0),
      minimum_notice_minutes: String(a.minimum_notice_minutes ?? 0), maximum_advance_days: String(a.maximum_advance_days ?? 60),
      in_person: (a.in_person as boolean) ?? true, telephone: (a.telephone as boolean) ?? false,
      video: (a.video as boolean) ?? false, onsite: (a.onsite as boolean) ?? false, active: (a.active as boolean) ?? true,
    });
    const w: Record<number, { start: string; end: string }> = {};
    ((a.availability as Record<string, unknown>[]) ?? []).forEach((av) => { w[av.day_of_week as number] = { start: av.start_time as string, end: av.end_time as string }; });
    setWindows(w); setOpen(true);
  };

  const toggleDay = (dow: number) => setWindows((w) => { const n = { ...w }; if (n[dow]) delete n[dow]; else n[dow] = { start: "08:00", end: "17:00" }; return n; });

  const save = async () => {
    if (form.name.trim().length < 1) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      const availability = Object.entries(windows).map(([dow, v]) => ({ day_of_week: Number(dow), start_time: v.start, end_time: v.end }));
      const body: Record<string, unknown> = {
        name: form.name.trim(), duration_minutes: Number(form.duration_minutes) || 60,
        buffer_before_minutes: Number(form.buffer_before_minutes) || 0, buffer_after_minutes: Number(form.buffer_after_minutes) || 0,
        minimum_notice_minutes: Number(form.minimum_notice_minutes) || 0, maximum_advance_days: Number(form.maximum_advance_days) || 60,
        in_person: form.in_person, telephone: form.telephone, video: form.video, onsite: form.onsite, active: form.active, availability,
      };
      if (editing) await api.atUpdate(editing.id as string, body); else await api.atCreate(body);
      toast.success("Appointment type saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id: string) => { try { await api.atDelete(id); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">How appointments are scheduled — duration, buffers, notice and availability.</p>
        <Button onClick={openNew} data-testid="at-add"><Plus className="h-4 w-4" /> Add appointment type</Button>
      </div>

      {rows === null ? <LoadingBlock rows={3} /> : rows.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No appointment types yet" description="Define bookable appointment types and their availability."
          action={<Button onClick={openNew} data-testid="at-empty-add"><Plus className="h-4 w-4" /> Add one</Button>} />
      ) : (
        <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="at-list">
          {rows.map((a) => (
            <div key={a.id as string} className="flex items-center justify-between px-5 py-4" data-testid={`at-item-${a.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-semibold text-slate-900">{a.name as string}</p>
                  {!(a.active as boolean) && <StatusBadge tone="slate">Inactive</StatusBadge>}
                  {MODALITIES.filter((m) => a[m.key]).map((m) => <StatusBadge key={m.key} tone="blue">{m.label}</StatusBadge>)}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{a.duration_minutes as number} min · notice {a.minimum_notice_minutes as number}m · up to {a.maximum_advance_days as number}d · {((a.availability as unknown[]) ?? []).length} day(s)</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`at-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setToDelete(a.id as string)} data-testid={`at-delete-${a.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
              </div>
            </div>
          ))}
        </CardContent></Card>
      )}

      <Modal open={open} onOpenChange={setOpen} title={editing ? "Edit appointment type" : "Add appointment type"} description="Scheduling rules and available hours." testid="at-modal">
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" required testid="field-at-name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="at-name" /></Field>
            <Field label="Duration (min)" testid="field-at-dur"><Input type="number" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} data-testid="at-duration" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Buffer before (min)" testid="field-at-bb"><Input type="number" value={form.buffer_before_minutes} onChange={(e) => set("buffer_before_minutes", e.target.value)} data-testid="at-buffer-before" /></Field>
            <Field label="Buffer after (min)" testid="field-at-ba"><Input type="number" value={form.buffer_after_minutes} onChange={(e) => set("buffer_after_minutes", e.target.value)} data-testid="at-buffer-after" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Minimum notice (min)" testid="field-at-notice"><Input type="number" value={form.minimum_notice_minutes} onChange={(e) => set("minimum_notice_minutes", e.target.value)} data-testid="at-min-notice" /></Field>
            <Field label="Max advance (days)" testid="field-at-adv"><Input type="number" value={form.maximum_advance_days} onChange={(e) => set("maximum_advance_days", e.target.value)} data-testid="at-max-advance" /></Field>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Modalities</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {MODALITIES.map((m) => (
                <label key={m.key} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2.5" data-testid={`at-modality-${m.key}`}>
                  <Checkbox checked={form[m.key as keyof typeof form] as boolean} onChange={(v) => set(m.key, v)} />
                  <span className="text-sm text-slate-700">{m.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Available days & hours</p>
            <div className="space-y-2" data-testid="at-availability">
              {DAYS.map((d) => (
                <div key={d.dow} className="flex items-center gap-3">
                  <label className="flex w-24 items-center gap-2"><Checkbox checked={!!windows[d.dow]} onChange={() => toggleDay(d.dow)} testid={`at-day-${d.dow}`} /><span className="text-sm text-slate-700">{d.label}</span></label>
                  {windows[d.dow] && (
                    <div className="flex items-center gap-2">
                      <Input type="time" value={windows[d.dow].start} onChange={(e) => setWindows((w) => ({ ...w, [d.dow]: { ...w[d.dow], start: e.target.value } }))} className="w-32" data-testid={`at-from-${d.dow}`} />
                      <span className="text-slate-400">to</span>
                      <Input type="time" value={windows[d.dow].end} onChange={(e) => setWindows((w) => ({ ...w, [d.dow]: { ...w[d.dow], end: e.target.value } }))} className="w-32" data-testid={`at-to-${d.dow}`} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2"><Checkbox checked={form.active} onChange={(v) => set("active", v)} testid="at-active" /><span className="text-sm text-slate-700">Active</span></label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="at-save">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Delete appointment type?"
        description="This removes the type and its availability." confirmLabel="Delete" destructive
        onConfirm={() => { if (toDelete) remove(toDelete); setToDelete(null); }} testid="at-delete-confirm" />
    </div>
  );
}
